import { useCallback, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import {
  CHANGE_REQUEST_REASSIGN_EMAIL_PREVIEW_QUERY,
  type ChangeRequestReassignEmailPreview,
  type ChangeRequestReassignEmailPreviewQueryResponse,
  type ChangeRequestReassignEmailPreviewQueryVariables
} from "../queries";

export function useChangeRequestReassignEmailPreview(changeRequestId: string) {
  const [preview, setPreview] = useState<ChangeRequestReassignEmailPreview | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(false);
  const activeRequestIdRef = useRef(0);

  const [fetchPreview] = useLazyQuery<
    ChangeRequestReassignEmailPreviewQueryResponse,
    ChangeRequestReassignEmailPreviewQueryVariables
  >(CHANGE_REQUEST_REASSIGN_EMAIL_PREVIEW_QUERY, {
    fetchPolicy: "network-only"
  });

  const resetPreview = useCallback(() => {
    activeRequestIdRef.current += 1;
    setPreview(null);
    setError(null);
    setLoading(false);
  }, []);

  const loadPreview = useCallback(
    async (targetSectionId: string) => {
      const requestId = activeRequestIdRef.current + 1;
      activeRequestIdRef.current = requestId;

      setLoading(true);
      setPreview(null);
      setError(null);

      const result = await fetchPreview({
        variables: {
          changeRequestId,
          targetSectionId
        }
      });

      if (requestId !== activeRequestIdRef.current) {
        return result;
      }

      if (result.error) {
        setError(result.error);
        setPreview(null);
      } else {
        setPreview(result.data?.changeRequestReassignEmailPreview ?? null);
        setError(null);
      }

      setLoading(false);
      return result;
    },
    [changeRequestId, fetchPreview]
  );

  return {
    loadPreview,
    resetPreview,
    preview,
    loading,
    error
  };
}
